/**
 * ImageUpload Component
 * Reusable component for uploading images with preview
 */

import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { adminApi, getImageUrl } from "services/api";
import { lightDark, DARK } from "utils/lightDark";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/gif"];

function ImageUpload({ currentImage, onImageChange, label = "Upload Image" }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage ? getImageUrl(currentImage) : null);
  const fileInputRef = useRef(null);

  // Sync preview with currentImage prop changes
  useEffect(() => {
    const imageUrl = currentImage ? getImageUrl(currentImage) : null;
    console.log("ImageUpload - currentImage:", currentImage, "-> preview:", imageUrl);
    setPreview(imageUrl);
  }, [currentImage]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    event.target.value = ""; // lets the same file be picked again
    return uploadFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (uploading) return;
    return uploadFile(event.dataTransfer?.files?.[0]);
  };

  const uploadFile = async (file) => {
    if (!file) return;

    // Validate file type: the server only accepts JPG, PNG and GIF
    if (!ACCEPTED_TYPES.includes(file.type)) {
      alert("Please choose a JPG, PNG or GIF image");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await adminApi.uploadImage(formData);

      // Update preview with server URL
      const serverUrl = getImageUrl(response.file.url);
      setPreview(serverUrl);

      // Call parent callback with the uploaded image URL
      if (onImageChange) {
        onImageChange(response.file.url);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert(error.response?.data?.error || "Failed to upload image");
      setPreview(currentImage ? getImageUrl(currentImage) : null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    if (onImageChange) {
      onImageChange("");
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <MDBox>
      <MDTypography variant="caption" fontWeight="regular" color="text" mb={1}>
        {label}
      </MDTypography>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      <Box
        onClick={!preview && !uploading ? handleClick : undefined}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        sx={{
          cursor: !preview && !uploading ? "pointer" : "default",
          border: lightDark("2px dashed #ddd", "2px dashed rgba(255, 255, 255, 0.24)"),
          borderRadius: 2,
          p: 2,
          textAlign: "center",
          position: "relative",
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          backgroundColor: lightDark("#f8f9fa", DARK.surface),
        }}
      >
        {uploading ? (
          <CircularProgress />
        ) : preview ? (
          <>
            <Box
              component="img"
              src={preview.startsWith("http") || preview.startsWith("/") ? preview : preview}
              alt="Preview"
              sx={{
                maxWidth: "100%",
                maxHeight: 200,
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
            <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Button
                size="medium"
                variant="contained"
                color="info"
                onClick={handleClick}
                startIcon={<Icon>edit</Icon>}
                sx={{
                  // The theme paints info/error contained buttons white with text.main
                  // text, which is near-white in dark mode.
                  color: lightDark("text.main", "dark.main"),
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                }}
              >
                Change Image
              </Button>
              <Button
                size="medium"
                variant="contained"
                color="error"
                onClick={handleRemove}
                startIcon={<Icon>delete</Icon>}
                sx={{
                  // The theme paints info/error contained buttons white with text.main
                  // text, which is near-white in dark mode.
                  color: lightDark("text.main", "dark.main"),
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                }}
              >
                Remove
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Icon sx={{ fontSize: 48, color: "#bbb", mb: 2 }}>cloud_upload</Icon>
            <MDTypography variant="body2" color="text" mb={2}>
              Click to upload or drag and drop
            </MDTypography>
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
              PNG, JPG, GIF up to 5MB
            </MDTypography>
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
              sx={{ mt: 2 }}
              startIcon={<Icon>upload</Icon>}
            >
              Choose File
            </Button>
          </>
        )}
      </Box>
    </MDBox>
  );
}

ImageUpload.propTypes = {
  currentImage: PropTypes.string,
  onImageChange: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default ImageUpload;
